import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  ChevronDown,
  ChevronRight,
  Play,
  TrendingUp,
} from "lucide-react";
import { useApi } from "../hooks/useApi";
import { settingsFromLive } from "./backtestingSettings";

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------
const fmtInt = (v) =>
  v == null || v === "" || Number.isNaN(Number(v))
    ? "–"
    : Math.round(Number(v)).toLocaleString("en-US");

const fmtNum = (v, dec = 2) =>
  v == null || v === "" || Number.isNaN(Number(v))
    ? "–"
    : Number(v).toLocaleString("en-US", {
        minimumFractionDigits: dec,
        maximumFractionDigits: dec,
      });

const fmtPct = (v) => (v == null || v === "" ? "–" : `${fmtNum(v, 1)}%`);

// Lead minutes -> "1h 30 min" / "45 min" / "2h".
const fmtLead = (m) => {
  const n = Math.round(Number(m));
  if (!Number.isFinite(n)) return "–";
  const h = Math.floor(n / 60);
  const mm = n % 60;
  if (h && mm) return `${h}h ${mm} min`;
  if (h) return `${h}h`;
  return `${mm} min`;
};

// BUY = green, SELL = red.
const sideColor = (dir) =>
  dir === "BUY" ? "text-emerald-600" : dir === "SELL" ? "text-rose-600" : "";

const signColor = (v, dark) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return dark ? "text-slate-300" : "text-slate-600";
  return n > 0
    ? dark
      ? "text-emerald-400"
      : "text-emerald-600"
    : dark
    ? "text-rose-400"
    : "text-rose-600";
};

// v4 stack — NO edge model, NO walk-forward. Selection = per-side SK floors
// (+ guards + lead window); the margins are NOT row filters any more:
// per-level IOC-limit execution (2026-07-12 cutover) fills book levels while
//   BUY:  price ≤ min(last_trade + anchor_tol, pred_cen − req)
//   SELL: mirror with the sell req
// so every filled MWh individually clears both the anchor band and the margin;
// partial fills replace the old all-or-nothing veto. Sign convention kept:
// NEGATIVE margins = REQUIRE POSITIVE ppro (required ppro = −margin; the
// production −10/−20 requires +10/+20). See EXECUTION_PREFIX_LIMIT_INSTRUCTIONS.md.
const DEFAULT_PARAMS = {
  // BUY SK floor is lead-dependent (the validated "BUY ramp"): buy_floor for
  // lead ≤ 60 min, buy_floor_mid for 60–120 min, buy_floor_long beyond. Equal
  // values = the old flat floor.
  buy_floor: 600,
  buy_floor_mid: 650,
  buy_floor_long: 800,
  buy_lead_split1: 60,
  buy_lead_split2: 120,
  sell_floor: 100,
  buy_margin: -10,
  sell_margin: -10,
  anchor_tol: 4,
  lead_min: 10,
  lead_max: 240,
  transaction_fee_rate: 0.05,
  volume_fee_rate: 0.45,
};

// Trend-conditioned SELL margin: when the contract's pred_sk moved by more
// than a threshold vs its previous decision (≤60 min back), the drop/rise
// margin replaces the base sell margin inside execution (validated defaults:
// margin 0 when pred_sk fell >50 MW — momentum into surplus; margin −20 when
// it rose >25 MW — the SELL call is dissolving). BUY margin stays constant.
const DEFAULT_TREND = {
  enabled: true,
  sell_margin_drop: 0,
  sell_margin_rise: -20,
  sell_dpred_drop: -50,
  sell_dpred_rise: 25,
};

// The stored per-candidate ladder only covers ±24 € around the anchor, so the
// anchor tolerance cannot honestly re-execute beyond that (backend rejects too).
const ANCHOR_TOL_MAX = 24;

// Guards — strategy-level trade filters validated July 2026 (the "stack F"
// recipe, STRATEGY_GUARDS_2026-07.md): block proposed trades under fundamental
// conditions where the SK model's calls are least trustworthy. Applied as plain
// WHERE conditions at query time; NULL fundamentals pass (same as ppro).
const GUARD_DEFS = [
  {
    key: "buy_subres",
    param: "guard_buy_subres",
    label: "Block BUY on RES revision",
    cond: "sub_res >",
    step: "50",
    tip:
      "sub_res = sub_solar + sub_wind (MW): how much the freshest renewables forecast was revised UP vs the plan, " +
      "as of the decision. Positive = surplus risk → BUY (shortage) calls fail more often. Missing data passes.",
  },
  {
    key: "buy_reserve",
    param: "guard_buy_reserve",
    label: "Block BUY on high reserve",
    cond: "bpkd_reserve >",
    step: "100",
    tip:
      "bpkd_reserve (MW): planned system reserve at the decision. Abundant reserve (> ~3500) = no shortage stress " +
      "→ weak BUYs. Missing data passes.",
  },
  {
    key: "sell_subzrb",
    param: "guard_sell_subzrb",
    label: "Block SELL on collapsed zrb",
    cond: "sub_zrb <",
    step: "100",
    tip:
      "sub_zrb (MW): balancing-demand revision. Deeply negative = the collapse is already public/priced-in " +
      "→ weak SELLs. Missing data passes.",
  },
];

// Initial display values; the saved LiveTesting configuration is loaded before
// the first backtest and is also used by Reset.
const DEFAULT_GUARDS = {
  buy_subres: { enabled: true, value: 0 },
  buy_reserve: { enabled: false, value: 3500 },
  sell_subzrb: { enabled: false, value: -1500 },
};

// CEB-ladder predictions only exist from this date; before it ppro is NULL (and
// passes), so the CEB margins don't bind. This is the default range start.
const CEB_START = "2026-01-01";

// Default date range for a cand_set: 2026-01-01 (or the set's first day if
// later) → yesterday (Warsaw), capped at the set's last day. Earlier dates are
// selectable but the CEB margins don't bind before 2026-01-01.
const warsawYesterday = () => {
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Warsaw" }).format(new Date());
  const d = new Date(`${today}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

const rangeForRow = (row) => {
  const minDay = String(row.min_day || "").slice(0, 10);
  const lo = minDay && minDay > CEB_START ? minDay : CEB_START;
  const maxDay = String(row.max_day || "").slice(0, 10);
  const yest = warsawYesterday();
  const hi = maxDay && maxDay < yest ? maxDay : yest;
  return [lo, hi];
};

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------
const KpiCard = ({ label, value, sub, accent, dark }) => (
  <div
    className={`rounded-xl border p-4 ${
      dark ? "bg-slate-800/60 border-slate-700" : "bg-white border-slate-200"
    }`}
  >
    <div className={`text-xs font-medium ${dark ? "text-slate-400" : "text-slate-500"}`}>
      {label}
    </div>
    <div className={`mt-1 text-2xl font-bold ${accent || (dark ? "text-slate-100" : "text-slate-800")}`}>
      {value}
    </div>
    {sub ? (
      <div className={`mt-0.5 text-xs ${dark ? "text-slate-500" : "text-slate-400"}`}>{sub}</div>
    ) : null}
  </div>
);

// Horizontal mini bar-chart row (no charting dependency).
const Bars = ({ rows, valueKey, labelKey, dark, fmt = fmtInt, unit = "" }) => {
  const max = Math.max(1, ...rows.map((r) => Math.abs(Number(r[valueKey]) || 0)));
  return (
    <div className="space-y-1.5">
      {rows.map((r, i) => {
        const v = Number(r[valueKey]) || 0;
        const pct = (Math.abs(v) / max) * 100;
        const pos = v >= 0;
        return (
          <div key={i} className="flex items-center gap-2 text-xs">
            <div className={`w-20 shrink-0 truncate ${dark ? "text-slate-400" : "text-slate-500"}`}>
              {r[labelKey]}
            </div>
            <div className={`flex-1 h-4 rounded ${dark ? "bg-slate-800" : "bg-slate-100"}`}>
              <div
                className={`h-4 rounded ${pos ? "bg-emerald-500/80" : "bg-rose-500/80"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className={`w-24 shrink-0 text-right font-medium ${signColor(v, dark)}`}>
              {fmt(v)}
              {unit}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Columns for the per-day product drill-down (header reused so it can repeat).
// "CEB margin ✓" replaces the v3 "Edge €/MWh" column — it shows `ppro`, the
// EUR/MWh by which the entry beats (or, negative, misses) the predicted CEN.
const DETAIL_COLS = [
  { label: "Product", align: "left" },
  { label: "Side", align: "left" },
  { label: "CEB margin ✓", align: "right" },
  { label: "Lead", align: "right" },
  { label: "Vol MWh", align: "right" },
  { label: "Entry €", align: "right" },
  { label: "Pred CEN €", align: "right" },
  { label: "RCE €", align: "right" },
  { label: "CEN settled €", align: "right" },
  { label: "SK pred", align: "right" },
  { label: "SK settled", align: "right" },
  { label: "CEB pred €", align: "right" },
  { label: "CEB settled €", align: "right" },
  { label: "P&L €", align: "right" },
  { label: "€/MWh", align: "right" },
];

const DetailHeaderRow = ({ dark, repeat }) => (
  <tr
    className={`${dark ? "text-slate-400" : "text-slate-500"} uppercase tracking-wide text-[10px] ${
      repeat ? (dark ? "border-t-2 border-slate-700" : "border-t-2 border-slate-300") : ""
    }`}
  >
    {DETAIL_COLS.map((c) => (
      <th key={c.label} className={`${c.align === "left" ? "text-left" : "text-right"} py-1.5 px-2`}>
        {c.label}
      </th>
    ))}
  </tr>
);

const SectionCard = ({ title, icon, children, dark, right }) => (
  <div
    className={`rounded-2xl border p-5 ${
      dark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200 shadow-sm"
    }`}
  >
    <div className="flex items-center justify-between mb-4">
      <h2 className={`flex items-center gap-2 text-sm font-semibold uppercase tracking-wide ${dark ? "text-slate-300" : "text-slate-600"}`}>
        {icon}
        {title}
      </h2>
      {right}
    </div>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
// `candSetOverride` pins the page to a specific candidate set (the v5.1 tab);
// without it the backend default set is used. `variantLabel` names the tab.
// `defaultParams`/`defaultTrend` override the per-tab starting configuration.
const Backtesting = ({
  darkMode: dark,
  candSetOverride = null,
  variantLabel = null,
  defaultParams = null,
  defaultTrend = null,
}) => {
  const { fetchGet, isAuthenticated } = useApi();

  const initialParams = { ...DEFAULT_PARAMS, ...(defaultParams || {}) };
  const initialTrend = { ...DEFAULT_TREND, ...(defaultTrend || {}) };
  const [meta, setMeta] = useState(null);
  const [candSet, setCandSet] = useState("");
  const [params, setParams] = useState(initialParams);
  const [trend, setTrend] = useState(initialTrend);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [guards, setGuards] = useState(DEFAULT_GUARDS);
  const [liveSettings, setLiveSettings] = useState(null);

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [openDay, setOpenDay] = useState(null);
  const [dayDetail, setDayDetail] = useState({}); // day -> rows | "loading"

  const labelCls = `block text-xs font-medium mb-1 ${dark ? "text-slate-400" : "text-slate-500"}`;
  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm ${
    dark
      ? "bg-slate-800 border-slate-700 text-slate-100 focus:border-blue-500"
      : "bg-white border-slate-300 text-slate-800 focus:border-blue-500"
  } focus:outline-none focus:ring-1 focus:ring-blue-500`;

  // ---- Build query string for the active strategy ----
  const queryString = useCallback(
    (extra = {}) => {
      // Enabled guards send their threshold; disabled ones send nothing
      // (absent param = guard off on the backend).
      const guardParams = {};
      GUARD_DEFS.forEach(({ key, param }) => {
        const g = guards[key];
        if (g?.enabled && g.value !== "" && g.value != null) guardParams[param] = g.value;
      });
      // Trend-conditioned SELL margin params are only sent when enabled
      // (absent = flat base sell margin on the backend).
      const trendParams = trend.enabled
        ? Object.fromEntries(Object.entries({
            sell_margin_drop: trend.sell_margin_drop,
            sell_margin_rise: trend.sell_margin_rise,
            sell_dpred_drop: trend.sell_dpred_drop,
            sell_dpred_rise: trend.sell_dpred_rise,
          }).filter(([, value]) => value !== null && value !== ""))
        : {};
      const qp = new URLSearchParams({
        cand_set: candSet,
        ...Object.fromEntries(Object.entries(params).map(([k, v]) => [k, v])),
        ...trendParams,
        ...guardParams,
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
        ...extra,
      });
      return qp.toString();
    },
    [candSet, params, trend, guards, start, end]
  );

  // ---- Load meta on mount ----
  useEffect(() => {
    if (!isAuthenticated) return;
    let alive = true;
    (async () => {
      try {
        const metaQs = candSetOverride
          ? `?cand_set=${encodeURIComponent(candSetOverride)}`
          : "";
        const [res, liveRes] = await Promise.all([
          fetchGet(`/backtesting_v4/meta${metaQs}`),
          fetchGet("/live/config"),
        ]);
        if (!res.ok || !liveRes.ok) throw new Error("Failed to load candidate metadata or LiveTesting settings.");
        const [data, liveData] = await Promise.all([res.json(), liveRes.json()]);
        const settings = settingsFromLive(liveData.config);
        if (!alive) return;
        setLiveSettings(settings);
        setParams({ ...DEFAULT_PARAMS, ...settings.params });
        setTrend(settings.trend);
        setGuards(settings.guards);
        setMeta(data);
        const cs = candSetOverride || data.default_cand_set || "";
        if (!data.cand_sets?.some((c) => c.cand_set === cs)) {
          throw new Error("The requested candidate set is unavailable. No older set was selected.");
        }
        setCandSet(cs);
        const csRow = data.cand_sets?.find((c) => c.cand_set === cs);
        if (csRow) {
          const [s, e] = rangeForRow(csRow);
          setStart(s);
          setEnd(e);
        }
      } catch (e) {
        if (alive) setError(e.message || "Failed to load backtest metadata or LiveTesting settings.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [isAuthenticated, fetchGet, candSetOverride]);

  // ---- Run backtest ----
  const runBacktest = useCallback(async () => {
    if (!candSet || !liveSettings) return;
    setLoading(true);
    setError(null);
    setOpenDay(null);
    setDayDetail({});
    try {
      const res = await fetchGet(`/backtesting_v4/run?${queryString()}`);
      const data = await res.json();
      if (data.status !== "success") throw new Error(data.detail || "run failed");
      setResult(data);
    } catch (e) {
      setError(e.message || "Backtest failed.");
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [candSet, fetchGet, queryString, liveSettings]);

  // Auto-run once meta + date range are ready.
  useEffect(() => {
    if (candSet && liveSettings && start && end && !result) runBacktest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candSet, liveSettings, start, end]);

  // ---- Drill-down: per-product detail for a day ----
  const toggleDay = useCallback(
    async (day) => {
      if (openDay === day) {
        setOpenDay(null);
        return;
      }
      setOpenDay(day);
      if (!dayDetail[day]) {
        setDayDetail((d) => ({ ...d, [day]: "loading" }));
        try {
          const res = await fetchGet(`/backtesting_v4/day?${queryString({ day })}`);
          const data = await res.json();
          setDayDetail((d) => ({ ...d, [day]: data.products || [] }));
        } catch (e) {
          setDayDetail((d) => ({ ...d, [day]: [] }));
        }
      }
    },
    [openDay, dayDetail, fetchGet, queryString]
  );

  const setParam = (key, value) => setParams((p) => ({ ...p, [key]: value }));

  // One volume, one model set: the cand_set is fixed to the backend default
  // (resolved on meta load); no selector in the UI.
  const summary = result?.summary;
  const byDay = useMemo(() => result?.by_day || [], [result]);
  const bySide = useMemo(() => result?.by_side || [], [result]);
  const byLead = result?.by_lead || [];
  const byMonth = result?.by_month || [];
  const bySk = result?.by_sk || [];
  const byProduct = result?.by_product || [];

  const skCorrect = bySk.find((r) => r.sk_correct === "true");
  const skWrong = bySk.find((r) => r.sk_correct === "false");

  const sideTotal = useMemo(() => {
    const t = bySide.reduce(
      (a, s) => ({
        trades: a.trades + Number(s.trades || 0),
        total_mwh: a.total_mwh + Number(s.total_mwh || 0),
        total_pnl_eur: a.total_pnl_eur + Number(s.total_pnl_eur || 0),
      }),
      { trades: 0, total_mwh: 0, total_pnl_eur: 0 }
    );
    return t;
  }, [bySide]);

  const cumMax = Math.max(1, ...byDay.map((d) => Math.abs(Number(d.cum_pnl_eur) || 0)));

  // Warn when the selected range starts before CEB predictions exist.
  const cebHint = start && start < CEB_START;

  // -------------------------------------------------------------------------
  return (
    <div className="space-y-5">
      <div>
        <h1 className={`text-2xl font-bold ${dark ? "text-slate-100" : "text-slate-800"}`}>
          Intraday Backtesting{variantLabel ? ` — ${variantLabel}` : ""}
        </h1>
        {candSetOverride && (
          <p className={`text-xs mt-1 ${dark ? "text-slate-500" : "text-slate-400"}`}>
            candidate set: {candSetOverride}
          </p>
        )}
      </div>

      {/* ---------------- Configuration ---------------- */}
      <SectionCard
        title="Configuration"
        icon={<Activity className="w-4 h-4" />}
        dark={dark}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className={labelCls}>Start Date</label>
            <input type="date" className={inputCls} value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>End Date</label>
            <input type="date" className={inputCls} value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>

        {cebHint ? (
          <div
            className={`rounded-lg border px-3 py-2 mb-4 text-[11px] ${
              dark ? "bg-amber-950/30 border-amber-900 text-amber-300" : "bg-amber-50 border-amber-200 text-amber-700"
            }`}
          >
            CEB-ladder predictions only exist from {CEB_START}. Before that date <code>pred_cen</code> is NULL, so the
            margin price-cap doesn't bind — only the SK floors and the anchor band apply to earlier days.
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className={labelCls}>Lead min (min before gate closure)</label>
            <input
              type="number"
              step="5"
              className={inputCls}
              value={params.lead_min}
              onChange={(e) => setParam("lead_min", e.target.value === "" ? "" : Number(e.target.value))}
            />
          </div>
          <div>
            <label className={labelCls}>Lead max (min before gate closure)</label>
            <input
              type="number"
              step="5"
              className={inputCls}
              value={params.lead_max}
              onChange={(e) => setParam("lead_max", e.target.value === "" ? "" : Number(e.target.value))}
            />
            <div className={`mt-1 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
              data spans ~10–180 min (240 = no cap)
            </div>
          </div>
        </div>

        <div
          className={`rounded-xl border p-4 mb-4 ${
            dark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"
          }`}
        >
          <div className={`text-xs font-semibold uppercase tracking-wide mb-3 ${dark ? "text-slate-400" : "text-slate-500"}`}>
            Selection & execution — SK floors + IOC-limit margins (v4)
          </div>
          <div className="mb-4">
            <label className={labelCls}>BUY SK floor by lead time (MW) — ≤{params.buy_lead_split1} min / {params.buy_lead_split1}–{params.buy_lead_split2} min / &gt;{params.buy_lead_split2} min</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                ["buy_floor", `≤ ${params.buy_lead_split1} min`],
                ["buy_floor_mid", `${params.buy_lead_split1}–${params.buy_lead_split2} min`],
                ["buy_floor_long", `> ${params.buy_lead_split2} min`],
              ].map(([key, cap]) => (
                <div key={key}>
                  <input
                    type="number"
                    step="10"
                    className={inputCls}
                    value={params[key]}
                    onChange={(e) => setParam(key, e.target.value === "" ? "" : Number(e.target.value))}
                  />
                  <div className={`mt-1 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>{cap}</div>
                </div>
              ))}
            </div>
            <div className={`mt-1 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
              keep BUYs with pred_sk ≥ the floor of their lead bucket (equal values = flat floor; the validated
              "BUY ramp" demands a stronger shortage signal further from delivery)
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className={labelCls}>SELL SK floor (MW)</label>
              <input
                type="number"
                step="10"
                className={inputCls}
                value={params.sell_floor}
                onChange={(e) => setParam("sell_floor", e.target.value === "" ? "" : Number(e.target.value))}
              />
              <div className={`mt-1 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
                keep SELLs with pred_sk ≤ −floor
              </div>
            </div>
            <div>
              <label className={labelCls}>Buy CEB margin (€/MWh)</label>
              <input
                type="number"
                step="1"
                className={inputCls}
                value={params.buy_margin}
                onChange={(e) => setParam("buy_margin", e.target.value === "" ? "" : Number(e.target.value))}
              />
              <div className={`mt-1 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
                min required profit €/MWh = −margin; levels priced worse than pred CEN − req are not filled
              </div>
            </div>
            <div>
              <label className={labelCls}>Sell CEB margin (€/MWh)</label>
              <input
                type="number"
                step="1"
                className={inputCls}
                value={params.sell_margin}
                onChange={(e) => setParam("sell_margin", e.target.value === "" ? "" : Number(e.target.value))}
              />
              <div className={`mt-1 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
                min required profit €/MWh = −margin; levels priced worse than pred CEN + req are not filled
              </div>
            </div>
            <div>
              <label className={labelCls}>Anchor tolerance (± €/MWh)</label>
              <input
                type="number"
                step="1"
                min="0"
                max={ANCHOR_TOL_MAX}
                className={inputCls}
                value={params.anchor_tol}
                onChange={(e) => setParam("anchor_tol", e.target.value === "" ? "" : Number(e.target.value))}
              />
              <div className={`mt-1 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
                band around last trade within which levels may be swept; production 8, max {ANCHOR_TOL_MAX}
              </div>
            </div>
          </div>

          {/* Trend-conditioned SELL margin (BUY margin stays constant). */}
          <div className={`mt-4 rounded-lg border p-3 ${dark ? "border-slate-700" : "border-slate-200"}`}>
            <label className={`flex items-center gap-2 cursor-pointer text-sm font-medium ${dark ? "text-slate-200" : "text-slate-700"}`}>
              <input
                type="checkbox"
                checked={trend.enabled}
                onChange={(e) => setTrend((t) => ({ ...t, enabled: e.target.checked }))}
                className="w-4 h-4 rounded accent-blue-600"
              />
              Trend-conditioned SELL margin (SK pred movement vs previous decision, ≤60 min back)
            </label>
            {trend.enabled ? (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ["sell_dpred_drop", "Drop threshold (MW)", "apply drop margin when Δpred_sk < this"],
                  ["sell_margin_drop", "Margin @ drop (€/MWh)", "0 = fill down to pred CEN (momentum into surplus)"],
                  ["sell_dpred_rise", "Rise threshold (MW)", "apply rise margin when Δpred_sk > this"],
                  ["sell_margin_rise", "Margin @ rise (€/MWh)", "stricter cap when the SELL call is dissolving"],
                ].map(([key, label, tip]) => (
                  <div key={key}>
                    <label className={labelCls}>{label}</label>
                    <input
                      type="number"
                      step={key.startsWith("sell_dpred") ? "5" : "1"}
                      className={inputCls}
                      value={trend[key] ?? ""}
                      onChange={(e) =>
                        setTrend((t) => ({ ...t, [key]: e.target.value === "" ? "" : Number(e.target.value) }))
                      }
                    />
                    <div className={`mt-1 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>{tip}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`mt-1 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
                disabled — the flat Sell CEB margin applies to every SELL fill
              </div>
            )}
          </div>
        </div>

        <div
          className={`rounded-xl border p-4 mb-4 ${
            dark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"
          }`}
        >
          <div className={`text-xs font-semibold uppercase tracking-wide mb-3 ${dark ? "text-slate-400" : "text-slate-500"}`}>
            Guards — fundamental trade filters
          </div>
          <div className="space-y-2.5">
            {GUARD_DEFS.map(({ key, label, cond, step, tip }) => {
              const g = guards[key];
              return (
                <div key={key} className="flex flex-wrap items-center gap-x-3 gap-y-1.5" title={tip}>
                  <label className={`flex items-center gap-2 w-64 cursor-pointer text-sm ${dark ? "text-slate-200" : "text-slate-700"}`}>
                    <input
                      type="checkbox"
                      checked={g.enabled}
                      onChange={(e) =>
                        setGuards((prev) => ({ ...prev, [key]: { ...prev[key], enabled: e.target.checked } }))
                      }
                      className="w-4 h-4 rounded accent-blue-600"
                    />
                    {label}
                  </label>
                  <span className={`w-32 font-mono text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>{cond}</span>
                  <input
                    type="number"
                    step={step}
                    disabled={!g.enabled}
                    value={g.value}
                    onChange={(e) =>
                      setGuards((prev) => ({
                        ...prev,
                        [key]: { ...prev[key], value: e.target.value === "" ? "" : Number(e.target.value) },
                      }))
                    }
                    className={`${inputCls} !w-28 disabled:opacity-40`}
                  />
                  <span className={`text-xs ${dark ? "text-slate-500" : "text-slate-400"}`}>MW</span>
                </div>
              );
            })}
          </div>
          <div className={`mt-3 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
            Guards block trades where the SK model is least reliable (validated July 2026). Missing data passes.
          </div>
        </div>

        <div
          className={`rounded-xl border p-4 mb-4 ${
            dark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"
          }`}
        >
          <div className={`text-xs font-semibold uppercase tracking-wide mb-3 ${dark ? "text-slate-400" : "text-slate-500"}`}>
            Fees (applied to the summary only)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Transaction Fee (%)</label>
              <input
                type="number"
                step="0.01"
                className={inputCls}
                value={params.transaction_fee_rate}
                onChange={(e) => setParam("transaction_fee_rate", e.target.value === "" ? "" : Number(e.target.value))}
              />
              <div className={`mt-1 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
                % of sell-side revenue (sold only, not bought)
              </div>
            </div>
            <div>
              <label className={labelCls}>Volume Fee (PLN/MWh)</label>
              <input
                type="number"
                step="0.1"
                className={inputCls}
                value={params.volume_fee_rate}
                onChange={(e) => setParam("volume_fee_rate", e.target.value === "" ? "" : Number(e.target.value))}
              />
              <div className={`mt-1 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
                flat PLN/MWh on all volume traded (buys &amp; sells) → converted to EUR
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={runBacktest}
            disabled={loading || !candSet || !liveSettings}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <Play className="w-4 h-4" />
            {loading ? "Running…" : "Run Backtest"}
          </button>
          <button
            onClick={() => {
              if (!liveSettings) return;
              setParams({ ...DEFAULT_PARAMS, ...liveSettings.params });
              setTrend({ ...liveSettings.trend });
              setGuards(liveSettings.guards);
            }}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
              dark
                ? "border-slate-700 text-slate-300 hover:bg-slate-800"
                : "border-slate-300 text-slate-600 hover:bg-slate-50"
            }`}
          >
            Reset to LiveTesting
          </button>
        </div>
      </SectionCard>

      {error ? (
        <div className="rounded-xl border border-rose-300 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
          {error}
        </div>
      ) : null}

      {/* ---------------- Summary ---------------- */}
      {summary ? (
        <SectionCard
          title="Summary"
          icon={<TrendingUp className="w-4 h-4" />}
          dark={dark}
        >
          {/* Headline strip */}
          <div className={`flex flex-wrap gap-x-8 gap-y-2 mb-5 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>
            <div>
              <span className="font-semibold">{fmtInt(summary.days)}</span> trading days ·{" "}
              <span className="text-emerald-600 font-semibold">{fmtInt(summary.profitable_days)} profitable</span> /{" "}
              <span className="text-rose-600 font-semibold">{fmtInt(summary.loss_days)} loss</span>
              {Number(summary.pending_days) > 0 ? (
                <>
                  {" / "}
                  <span className="text-amber-600 font-semibold">
                    {fmtInt(summary.pending_days)} awaiting settlement
                  </span>
                </>
              ) : null}
              {summary.no_trade_days != null ? (
                <span className={dark ? "text-slate-500" : "text-slate-400"}>
                  {" "}
                  · {fmtInt(summary.no_trade_days)} without trades
                </span>
              ) : null}
            </div>
            <div>
              <span className="font-semibold">{fmtInt(summary.trades)}</span> trades
            </div>
            <div>
              Avg Daily P&amp;L:{" "}
              <span className={`font-semibold ${signColor(summary.daily_avg_pnl, dark)}`}>
                {fmtInt(summary.daily_avg_pnl)} EUR
              </span>
            </div>
            <div>
              Avg CEB margin:{" "}
              <span className="font-semibold">{fmtNum(summary.avg_ppro, 2)} €/MWh</span>
            </div>
            <div
              title={`Transaction fee: ${summary.transaction_fee_rate}% of sell-side revenue = €${fmtInt(summary.transaction_fee_eur)}. Volume fee: ${summary.volume_fee_rate} PLN/MWh on all traded volume = €${fmtInt(summary.volume_fee_eur)} (EUR/PLN ${summary.eur_pln ?? "–"}).`}
            >
              Fees:{" "}
              <span className="font-semibold text-rose-600">−{fmtInt(summary.total_fees_eur)} EUR</span>
              <span className={`ml-1 ${dark ? "text-slate-500" : "text-slate-400"}`}>
                (transaction fee €{fmtInt(summary.transaction_fee_eur)} + volume fee €{fmtInt(summary.volume_fee_eur)})
              </span>
            </div>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3 mb-5">
            <KpiCard dark={dark} label="Total P&L" value={`${fmtInt(summary.total_pnl_eur)}`} sub="EUR (gross)" accent={signColor(summary.total_pnl_eur, dark)} />
            <KpiCard dark={dark} label="PnL After Fees" value={`${fmtInt(summary.pnl_after_fees_eur)}`} sub={`EUR · −${fmtInt(summary.total_fees_eur)} fees`} accent={signColor(summary.pnl_after_fees_eur, dark)} />
            <KpiCard dark={dark} label="EUR / MWh" value={fmtNum(summary.eur_per_mwh, 2)} accent={signColor(summary.eur_per_mwh, dark)} />
            <KpiCard dark={dark} label="Win Rate" value={fmtPct(summary.win_pct)} />
            <KpiCard dark={dark} label="Trades" value={fmtInt(summary.trades)} />
            <KpiCard dark={dark} label="Total Volume" value={fmtInt(summary.total_mwh)} sub="MWh" />
            <KpiCard dark={dark} label="Avg MWh / Day" value={fmtInt(summary.daily_avg_mwh)} sub="MWh" />
          </div>

          {/* BY SIDE table (mirrors the dashboard's BY STRATEGY table) */}
          <div className="overflow-x-auto">
            <table className="app-table w-full text-sm">
              <thead>
                <tr className={`${dark ? "text-slate-400" : "text-slate-500"} text-xs uppercase tracking-wide`}>
                  <th className="text-left font-medium py-2 px-3">By Side</th>
                  <th className="text-right font-medium py-2 px-3 text-emerald-600">BUY · shortage</th>
                  <th className="text-right font-medium py-2 px-3 text-rose-600">SELL · surplus</th>
                  <th className="text-right font-medium py-2 px-3">Total</th>
                </tr>
              </thead>
              <tbody className={dark ? "text-slate-200" : "text-slate-700"}>
                {[
                  { label: "P&L (EUR)", get: (s) => s?.total_pnl_eur, tot: sideTotal.total_pnl_eur, money: true },
                  { label: "Volume (MWh)", get: (s) => s?.total_mwh, tot: sideTotal.total_mwh },
                  { label: "Trades", get: (s) => s?.trades, tot: sideTotal.trades },
                  {
                    label: "EUR / MWh",
                    get: (s) => s?.eur_per_mwh,
                    tot: sideTotal.total_mwh ? sideTotal.total_pnl_eur / sideTotal.total_mwh : null,
                    dec: 2,
                  },
                  { label: "Win %", get: (s) => s?.win_pct, tot: null, pct: true },
                ].map((row) => {
                  const buy = bySide.find((s) => s.side === "BUY");
                  const sell = bySide.find((s) => s.side === "SELL");
                  const cell = (v) =>
                    row.pct ? fmtPct(v) : row.dec != null ? fmtNum(v, row.dec) : fmtInt(v);
                  return (
                    <tr key={row.label} className={`border-t ${dark ? "border-slate-800" : "border-slate-100"}`}>
                      <td className={`py-2 px-3 text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>{row.label}</td>
                      <td className={`py-2 px-3 text-right ${row.money ? signColor(buy?.total_pnl_eur, dark) : ""}`}>{cell(row.get(buy))}</td>
                      <td className={`py-2 px-3 text-right ${row.money ? signColor(sell?.total_pnl_eur, dark) : ""}`}>{cell(row.get(sell))}</td>
                      <td className={`py-2 px-3 text-right font-semibold ${row.money ? signColor(row.tot, dark) : ""}`}>
                        {row.tot == null ? "–" : cell(row.tot)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCard>
      ) : null}

      {/* ---------------- Diagnostic cards (SK accuracy + breakdowns) ---------------- */}
      {summary ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* SK-correct */}
          <div className={`rounded-2xl border p-5 ${dark ? "bg-emerald-950/30 border-emerald-900" : "bg-emerald-50 border-emerald-200"}`}>
            <div className={`text-sm font-semibold ${dark ? "text-emerald-300" : "text-emerald-700"}`}>Win Rate · SK Correct</div>
            <div className={`mt-1 text-3xl font-bold ${dark ? "text-emerald-300" : "text-emerald-700"}`}>{fmtPct(skCorrect?.win_pct)}</div>
            <div className={`text-xs mt-1 ${dark ? "text-emerald-400/70" : "text-emerald-600/80"}`}>
              {fmtInt(skCorrect?.trades)} trades · {fmtInt(skCorrect?.total_pnl_eur)} EUR
            </div>
          </div>
          {/* SK-wrong */}
          <div className={`rounded-2xl border p-5 ${dark ? "bg-rose-950/30 border-rose-900" : "bg-rose-50 border-rose-200"}`}>
            <div className={`text-sm font-semibold ${dark ? "text-rose-300" : "text-rose-700"}`}>Win Rate · SK Wrong</div>
            <div className={`mt-1 text-3xl font-bold ${dark ? "text-rose-300" : "text-rose-700"}`}>{fmtPct(skWrong?.win_pct)}</div>
            <div className={`text-xs mt-1 ${dark ? "text-rose-400/70" : "text-rose-600/80"}`}>
              {fmtInt(skWrong?.trades)} trades · {fmtInt(skWrong?.total_pnl_eur)} EUR
            </div>
            <div className={`text-[11px] mt-2 ${dark ? "text-slate-400" : "text-slate-500"}`}>
              Losses concentrate where the SK signal mis-predicts direction — SK accuracy is the binding lever.
            </div>
          </div>
          {/* P&L by lead bucket */}
          <SectionCard title="P&L by Lead Window" icon={<Activity className="w-4 h-4" />} dark={dark}>
            <Bars rows={byLead} valueKey="total_pnl_eur" labelKey="bucket" dark={dark} fmt={fmtInt} unit="" />
          </SectionCard>
        </div>
      ) : null}

      {/* ---------------- SK direction split on realized trades ---------------- */}
      {summary ? (
        (() => {
          const nCorrect = Number(skCorrect?.trades) || 0;
          const nWrong = Number(skWrong?.trades) || 0;
          const realized = nCorrect + nWrong;
          const correctPct = realized ? (nCorrect / realized) * 100 : 0;
          const wrongPct = realized ? (nWrong / realized) * 100 : 0;
          const skRows = [
            { key: "Correct", n: nCorrect, share: correctPct, pnl: skCorrect?.total_pnl_eur, eurmwh: skCorrect?.eur_per_mwh, win: skCorrect?.win_pct, pos: true },
            { key: "Wrong", n: nWrong, share: wrongPct, pnl: skWrong?.total_pnl_eur, eurmwh: skWrong?.eur_per_mwh, win: skWrong?.win_pct, pos: false },
          ];
          const realizedMwh = (Number(skCorrect?.total_mwh) || 0) + (Number(skWrong?.total_mwh) || 0);
          const realizedPnl = (Number(skCorrect?.total_pnl_eur) || 0) + (Number(skWrong?.total_pnl_eur) || 0);
          const totalEurMwh = realizedMwh ? realizedPnl / realizedMwh : null;
          return (
            <SectionCard title="SK Direction · Realized Trades" icon={<Activity className="w-4 h-4" />} dark={dark}>
              <div className={`mb-3 text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>
                Of <span className="font-semibold">{fmtInt(realized)}</span> realized (settled) trades, the SK signal called
                direction correctly on <span className="text-emerald-600 font-semibold">{fmtPct(correctPct)}</span> and
                wrongly on <span className="text-rose-600 font-semibold">{fmtPct(wrongPct)}</span>.
              </div>
              {/* Stacked share bar */}
              <div className={`flex h-6 rounded-lg overflow-hidden mb-4 text-[11px] font-semibold text-white`}>
                <div className="bg-emerald-500 flex items-center justify-center" style={{ width: `${correctPct}%` }}>
                  {correctPct >= 12 ? `${fmtInt(nCorrect)} (${fmtPct(correctPct)})` : ""}
                </div>
                <div className="bg-rose-500 flex items-center justify-center" style={{ width: `${wrongPct}%` }}>
                  {wrongPct >= 12 ? `${fmtInt(nWrong)} (${fmtPct(wrongPct)})` : ""}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="app-table w-full text-sm">
                  <thead>
                    <tr className={`${dark ? "text-slate-400" : "text-slate-500"} text-xs uppercase tracking-wide`}>
                      <th className="text-left font-medium py-2 px-3">SK Direction</th>
                      <th className="text-right font-medium py-2 px-3">Trades</th>
                      <th className="text-right font-medium py-2 px-3">% of Realized</th>
                      <th className="text-right font-medium py-2 px-3">P&L €</th>
                      <th className="text-right font-medium py-2 px-3">Avg €/MWh</th>
                      <th className="text-right font-medium py-2 px-3">Win %</th>
                    </tr>
                  </thead>
                  <tbody className={dark ? "text-slate-200" : "text-slate-700"}>
                    {skRows.map((r) => (
                      <tr key={r.key} className={`border-t ${dark ? "border-slate-800" : "border-slate-100"}`}>
                        <td className={`py-2 px-3 font-medium ${r.pos ? "text-emerald-600" : "text-rose-600"}`}>{r.key}</td>
                        <td className="py-2 px-3 text-right font-medium">{fmtInt(r.n)}</td>
                        <td className="py-2 px-3 text-right">{fmtPct(r.share)}</td>
                        <td className={`py-2 px-3 text-right font-medium ${signColor(r.pnl, dark)}`}>{fmtInt(r.pnl)}</td>
                        <td className={`py-2 px-3 text-right ${signColor(r.eurmwh, dark)}`}>{fmtNum(r.eurmwh, 2)}</td>
                        <td className="py-2 px-3 text-right">{fmtPct(r.win)}</td>
                      </tr>
                    ))}
                    <tr className={`border-t ${dark ? "border-slate-700" : "border-slate-200"} font-semibold`}>
                      <td className="py-2 px-3">Total realized</td>
                      <td className="py-2 px-3 text-right">{fmtInt(realized)}</td>
                      <td className="py-2 px-3 text-right">100.0%</td>
                      <td className={`py-2 px-3 text-right ${signColor(realizedPnl, dark)}`}>
                        {fmtInt(realizedPnl)}
                      </td>
                      <td className={`py-2 px-3 text-right ${signColor(totalEurMwh, dark)}`}>{fmtNum(totalEurMwh, 2)}</td>
                      <td className="py-2 px-3 text-right">
                        {fmtPct(realized ? (nCorrect * Number(skCorrect?.win_pct || 0) + nWrong * Number(skWrong?.win_pct || 0)) / realized : 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </SectionCard>
          );
        })()
      ) : null}

      {/* ---------------- Charts: cumulative + by month ---------------- */}
      {summary ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SectionCard title="Cumulative P&L" icon={<TrendingUp className="w-4 h-4" />} dark={dark}>
            {/* No min-width on bars and no gaps on mobile: with ~200 days a
                2px floor + 1px gaps add up past narrow viewports and overflow.
                Bars shrink to fractional px instead (reads as an area chart). */}
            <div className="flex items-end gap-0 sm:gap-px h-40 overflow-hidden">
              {byDay.map((d, i) => {
                const v = Number(d.cum_pnl_eur) || 0;
                const h = (Math.abs(v) / cumMax) * 100;
                return (
                  <div
                    key={i}
                    title={`${String(d.day).slice(0, 10)}: ${fmtInt(v)} EUR`}
                    className={`flex-1 min-w-0 rounded-t ${v >= 0 ? "bg-emerald-500/70" : "bg-rose-500/70"}`}
                    style={{ height: `${Math.max(2, h)}%` }}
                  />
                );
              })}
            </div>
            <div className={`mt-2 flex justify-between text-[11px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
              <span>{byDay[0] ? String(byDay[0].day).slice(0, 10) : ""}</span>
              <span className={`font-semibold ${signColor(summary.total_pnl_eur, dark)}`}>
                {fmtInt(summary.total_pnl_eur)} EUR cumulative
              </span>
              <span>{byDay.length ? String(byDay[byDay.length - 1].day).slice(0, 10) : ""}</span>
            </div>
          </SectionCard>
          <SectionCard title="P&L by Month" icon={<Activity className="w-4 h-4" />} dark={dark}>
            <Bars rows={byMonth} valueKey="total_pnl_eur" labelKey="month" dark={dark} fmt={fmtInt} />
          </SectionCard>
        </div>
      ) : null}

      {/* ---------------- Daily Results ---------------- */}
      {summary ? (
        <SectionCard title="Daily Results" icon={<Activity className="w-4 h-4" />} dark={dark}>
          <div className="space-y-1.5">
            {byDay.length === 0 ? (
              <div className={`text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>No trades for this strategy.</div>
            ) : null}
            {byDay.map((d) => {
              const day = String(d.day).slice(0, 10);
              const isOpen = openDay === day;
              const detail = dayDetail[day];
              const pos = Number(d.total_pnl_eur) >= 0;
              // Background tint only on the day row itself (not the expanded body).
              const rowBg = pos
                ? dark
                  ? "bg-emerald-950/20"
                  : "bg-emerald-50/60"
                : dark
                ? "bg-rose-950/20"
                : "bg-rose-50/60";
              return (
                <div
                  key={day}
                  className={`rounded-lg border overflow-hidden ${
                    dark ? "border-slate-800" : "border-slate-200"
                  }`}
                >
                  <button
                    onClick={() => toggleDay(day)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm ${rowBg}`}
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                    <span className={`font-semibold w-24 text-left ${dark ? "text-slate-200" : "text-slate-700"}`}>{day}</span>
                    <span className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${dark ? "bg-slate-800 text-slate-300" : "bg-slate-200 text-slate-600"}`}>
                      {fmtInt(d.trades)} trades
                    </span>
                    <span className={`ml-auto flex flex-wrap items-center justify-end gap-x-5 gap-y-0.5 text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>
                      <span>Vol: <span className="font-medium">{fmtInt(d.total_mwh)}</span> MWh</span>
                      <span>
                        <span className="text-emerald-600 font-medium">BUY {fmtInt(d.buy)}</span>
                        {" · "}
                        <span className="text-rose-600 font-medium">SELL {fmtInt(d.sell)}</span>
                      </span>
                      <span>Win: <span className="font-medium">{fmtPct(d.win_pct)}</span></span>
                      <span>€/MWh: <span className="font-medium">{fmtNum(d.eur_per_mwh, 2)}</span></span>
                      <span className={`font-bold text-sm ${signColor(d.total_pnl_eur, dark)}`}>{fmtInt(d.total_pnl_eur)} EUR</span>
                    </span>
                  </button>

                  {isOpen ? (
                    <div className={`border-t px-3 py-3 ${dark ? "border-slate-800" : "border-slate-200"}`}>
                      {detail === "loading" || detail === undefined ? (
                        <div className={`text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>Loading products…</div>
                      ) : detail.length === 0 ? (
                        <div className={`text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>No products.</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="app-table w-full text-xs whitespace-nowrap">
                            <thead>
                              <DetailHeaderRow dark={dark} />
                            </thead>
                            <tbody className={dark ? "text-slate-200" : "text-slate-700"}>
                              {detail.map((r, i) => {
                                // SK pred & settled in red when their signs disagree.
                                const skOpp =
                                  r.pred_sk != null &&
                                  r.sk_settled != null &&
                                  Number(r.pred_sk) * Number(r.sk_settled) < 0;
                                const skCls = skOpp ? "text-rose-600 font-medium" : "";
                                // Hourly (PH) rows get a slight tint; QH stays white.
                                const isPH = String(r.product || "").startsWith("PH");
                                const rowTint = isPH ? (dark ? "bg-slate-800/40" : "bg-slate-50") : "";
                                // Repeat the header every 20 rows (only when the
                                // table runs beyond 20 rows).
                                const repeatHeader = detail.length > 20 && i > 0 && i % 20 === 0;
                                const dataRow = (
                                  <tr key={i} className={`border-t ${dark ? "border-slate-800" : "border-slate-100"} ${rowTint}`}>
                                    <td className="py-1.5 px-2 font-semibold">
                                      {r.product || r.contract_id}
                                    </td>
                                    <td className={`py-1.5 px-2 font-medium ${sideColor(r.direction)}`}>{r.direction}</td>
                                    <td className={`py-1.5 px-2 text-right font-medium ${r.ppro == null ? (dark ? "text-slate-500" : "text-slate-400") : signColor(r.ppro, dark)}`}>{r.ppro == null ? "NULL" : fmtNum(r.ppro, 2)}</td>
                                    <td className="py-1.5 px-2 text-right">{fmtLead(r.lead_min)}</td>
                                    <td className="py-1.5 px-2 text-right">{fmtNum(r.fill_mwh, 2)}</td>
                                    <td className="py-1.5 px-2 text-right">{fmtNum(r.entry_eur, 2)}</td>
                                    <td className="py-1.5 px-2 text-right">{fmtNum(r.pred_cen_eur, 2)}</td>
                                    <td className="py-1.5 px-2 text-right">{fmtNum(r.rce_eur, 2)}</td>
                                    <td className="py-1.5 px-2 text-right">{fmtNum(r.cen_settled_eur, 2)}</td>
                                    <td className={`py-1.5 px-2 text-right ${skCls}`}>{fmtInt(r.pred_sk)}</td>
                                    <td className={`py-1.5 px-2 text-right ${skCls}`}>{fmtInt(r.sk_settled)}</td>
                                    <td className="py-1.5 px-2 text-right">{fmtNum(r.pred_ceb_eur, 2)}</td>
                                    <td className="py-1.5 px-2 text-right">{fmtNum(r.ceb_settled_eur, 2)}</td>
                                    <td className={`py-1.5 px-2 text-right font-semibold ${signColor(r.pnl_eur, dark)}`}>{fmtNum(r.pnl_eur, 1)}</td>
                                    <td className={`py-1.5 px-2 text-right ${signColor(r.eur_per_mwh, dark)}`}>{fmtNum(r.eur_per_mwh, 2)}</td>
                                  </tr>
                                );
                                return repeatHeader ? (
                                  <React.Fragment key={i}>
                                    <DetailHeaderRow dark={dark} repeat />
                                    {dataRow}
                                  </React.Fragment>
                                ) : (
                                  dataRow
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </SectionCard>
      ) : null}

      {/* ---------------- Trades by Product ---------------- */}
      {summary && byProduct.length ? (
        <SectionCard
          title="Trades by Product"
          icon={<Activity className="w-4 h-4" />}
          dark={dark}
          right={
            <div className={`flex items-center gap-3 text-[11px] ${dark ? "text-slate-400" : "text-slate-500"}`}>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" /> BUY</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-rose-500" /> SELL</span>
            </div>
          }
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5">
            {["PH", "QH"].map((kind) => {
              const rows = byProduct.filter((r) => r.kind === kind);
              if (!rows.length) return null;
              const trades = rows.reduce((a, r) => a + Number(r.trades || 0), 0);
              const buy = rows.reduce((a, r) => a + Number(r.buy || 0), 0);
              const sell = rows.reduce((a, r) => a + Number(r.sell || 0), 0);
              return (
                <div key={kind}>
                  <div className={`mb-2 text-xs font-semibold uppercase tracking-wide ${dark ? "text-slate-400" : "text-slate-500"}`}>
                    {kind === "PH" ? "Hourly products (PH)" : "Quarter-hour products (QH)"}
                    <span className="ml-2 font-normal normal-case">
                      {fmtInt(trades)} trades · <span className="text-emerald-600">{fmtInt(buy)} BUY</span> / <span className="text-rose-600">{fmtInt(sell)} SELL</span>
                    </span>
                  </div>
                  <table className="app-table w-full text-xs">
                    <thead>
                      <tr className={`${dark ? "text-slate-400" : "text-slate-500"} uppercase tracking-wide text-[10px]`}>
                        <th className="text-left py-1.5 px-2">Product</th>
                        <th className="text-right py-1.5 px-2">Trades</th>
                        <th className="text-right py-1.5 px-2 text-emerald-600">BUY</th>
                        <th className="text-right py-1.5 px-2 text-rose-600">SELL</th>
                        <th className="text-right py-1.5 px-2">P&L €</th>
                        <th className="text-right py-1.5 px-2">Win %</th>
                      </tr>
                    </thead>
                    <tbody className={dark ? "text-slate-200" : "text-slate-700"}>
                      {rows.map((r) => (
                        <tr key={r.product} className={`border-t ${dark ? "border-slate-800" : "border-slate-100"}`}>
                          <td className="py-1.5 px-2 font-semibold">{r.product}</td>
                          <td className="py-1.5 px-2 text-right font-medium">{fmtInt(r.trades)}</td>
                          <td className="py-1.5 px-2 text-right text-emerald-600">{fmtInt(r.buy)}</td>
                          <td className="py-1.5 px-2 text-right text-rose-600">{fmtInt(r.sell)}</td>
                          <td className={`py-1.5 px-2 text-right font-medium ${signColor(r.total_pnl_eur, dark)}`}>{fmtInt(r.total_pnl_eur)}</td>
                          <td className="py-1.5 px-2 text-right">{fmtPct(r.win_pct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </SectionCard>
      ) : null}

      {loading && !result ? (
        <div className={`text-center py-10 text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>Running backtest…</div>
      ) : null}
    </div>
  );
};

export default Backtesting;
